import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	SlateViewer,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { PermissionName } from "../../../shared/types/permission";
import { consentService } from "../shared/api/consentService";
import { ConsentIndexResource } from "../shared/types/consent";
import { ConsentCreateDrawer, ConsentUpdateDrawer } from "./ConsentDrawer";

const SlateWrapper = styled.div`
	overflow: hidden;
`;

export const ConsentList: FC = () => {
	const { t } = useTranslation();
	const crudOperations = useLaravelCrudEntity(consentService, {
		entityName: "Consent",
		messages: {
			create: "Consent was saved.",
			update: "Consent was saved.",
			delete: "Consent was deleted.",
		},
	});
	const canCreate = useHasPermission<PermissionName>(["consent.create"]);
	const canUpdate = useHasPermission<PermissionName>(["consent.update"]);

	const columns: IDrawerDataListColumn<ConsentIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				type: "value",
				valueKey: "name",
			},
			{
				heading: t("Type"),
				type: "value",
				valueKey: "type",
			},
			{
				heading: t("Consent"),
				type: "value",
				valueKey: "consent",
				render: (consent) => (
					<SlateWrapper>
						<SlateViewer content={consent.consent} />
					</SlateWrapper>
				),
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: (consent, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Edit consent")}
							label={t("Edit consent")}
							action={openDrawer}
							icon="edit"
							shortcut="primary"
						/>
						<ContextMenuDeleteItem
							action={() => crudOperations.deleteEntity(consent.id)}
							title={t("Delete consent")}
							label={t("Delete consent")}
							heading={t("Delete")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
						>
							{t("Are you sure you want to delete this consent?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[crudOperations, t],
	);

	return (
		<PaginatedDrawerAutoDataList
			canCreate={canCreate}
			canUpdate={canUpdate}
			translations={{ empty: t("No consents available."), title: t("Consents") }}
			columns={columns}
			listFetchConfig={laravelPaginatedListFetchConfig(consentService.listKey, consentService.index)}
			renderCreateDrawer={(drawerProps) => (
				<ConsentCreateDrawer {...drawerProps} onSubmit={(values) => crudOperations.createEntity(values)} />
			)}
			renderUpdateDrawer={(drawerProps) => (
				<ConsentUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, entityFormState)}
				/>
			)}
		/>
	);
};
